import React, { useEffect, useState } from 'react';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import EmptyContent from 'components/EmptyContent/EmptyContent';
import useClinicService from 'hooks/useClinicService';
import { IService } from 'interfaces/services';
import ServicePanel from './ServicePanel/ServicePanel';
import MainSection from './MainSection/MainSection';
import { ServiceSidebarProvider } from './context/serviceSidebarContext';
import useClinic from 'hooks/useClinic';
import { PRACTICE_FILTER_OPTIONS } from 'utils/constants';
import useOperatory from 'hooks/useOperatory';

const { ALL, ACTIVE } = PRACTICE_FILTER_OPTIONS;

const RevampServicePage = () => {
  const {
    data: services,
    isLoading,
    mutate,
  } = useClinicService({
    sort: 'desc,asc',
    sortBy: 'status,order',
  });

  const { data: operatories } = useOperatory();

  const isHygieneChairSetup = operatories?.some((item) => item.isHygieneChair);

  const { data: clinic } = useClinic();

  const [selectedService, setSelectedService] = useState<IService | null>(null);

  const [isExamCleaningExpanded, setIsExamCleaningExpanded] = useState(false);

  const getTotalServiceByStatus = () => {
    if (!services) return null;
    const totalActive = services.data.filter((s) => s.isActive).length;
    const totalInactive = services.data.filter((s) => !s.isActive).length;

    return {
      active: totalActive,
      inactive: totalInactive,
      all: services.metadata.total,
    };
  };

  const [selectedFilter, setSelectedFilter] = useState(() => {
    const totalServiceByStatus = getTotalServiceByStatus();

    return totalServiceByStatus?.active === 0 ? ALL : ACTIVE;
  });

  useEffect(() => {
    if (!services || services.metadata.total === 0) return;
    if (selectedService) return;

    if (selectedFilter === ALL) {
      setSelectedService(services.data[0]);
    } else {
      const items = services.data.filter((s) =>
        selectedFilter === ACTIVE ? s.isActive : !s.isActive
      );

      setSelectedService(items[0] || services.data[0]);
    }
  }, [services, selectedService, selectedFilter]);

  if (isLoading) {
    return (
      <LoadingSpinner className="mx-auto mt-4.8 w-4.8 h-4.8 text-magenta" />
    );
  }

  if (services?.metadata.total === 0) {
    return <EmptyContent labelFor="practitioner" />;
  }

  return (
    <ServiceSidebarProvider>
      <div className="flex">
        <ServicePanel
          key={`${selectedFilter}-${selectedService?.updatedAt}-${selectedService?.name}`}
          services={services!.data}
          selectedService={selectedService!}
          setSelectedService={setSelectedService}
          totalServiceByStatus={getTotalServiceByStatus()}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
          isExamCleaningExpanded={isExamCleaningExpanded}
          setIsExamCleaningExpanded={setIsExamCleaningExpanded}
        />

        {selectedService && (
          <MainSection
            key={`${selectedService.id}-${selectedService.updatedAt}`}
            selectedService={selectedService}
            setSelectedService={setSelectedService}
            clinic={clinic}
            isHygieneChairSetup={Boolean(isHygieneChairSetup)}
            services={services?.data ?? []}
            mutate={mutate}
          />
        )}
      </div>
    </ServiceSidebarProvider>
  );
};

export default RevampServicePage;
export { RevampServicePage };
